import React from "react";
import { Prose } from "@/components/site/Prose";
import { useI18n } from "@/i18n/I18nContext";
import { Plane, Coins, Sun, Heart, ShieldCheck } from "lucide-react";

const ICONS = [Sun, Plane, Coins, ShieldCheck, Heart];

const QUICK_BY_LANG = {
  en: [
    { title: "Best Season", text: "October – April. Spring & autumn for diving." },
    { title: "How to Arrive", text: "Hurghada (HRG) 1.5h or Marsa Alam (RMF) 1h drive." },
    { title: "Currency", text: "Egyptian Pound (EGP). USD/EUR widely accepted." },
    { title: "Safety", text: "Quiet, family-friendly. Stay reef-aware on dives." },
    { title: "Etiquette", text: "Dress modestly in town. Say 'salam' to greet friendly." },
  ],
  de: [
    { title: "Beste Reisezeit", text: "Oktober – April. Frühling und Herbst sind ideal zum Tauchen." },
    { title: "Anreise", text: "Hurghada (HRG) 1,5h oder Marsa Alam (RMF) 1h Fahrt." },
    { title: "Währung", text: "Ägyptisches Pfund (EGP). USD/EUR werden weitgehend akzeptiert." },
    { title: "Sicherheit", text: "Ruhig, familienfreundlich. Beim Tauchen aufs Riff achten." },
    { title: "Etikette", text: "In der Stadt dezent kleiden. 'Salam' als freundliche Begrüßung." },
  ],
  fr: [
    { title: "Meilleure saison", text: "Octobre – avril. Printemps et automne idéaux pour la plongée." },
    { title: "Comment venir", text: "Hurghada (HRG) 1h30 ou Marsa Alam (RMF) 1h de route." },
    { title: "Monnaie", text: "Livre égyptienne (EGP). USD/EUR largement acceptés." },
    { title: "Sécurité", text: "Calme, familial. Respectez les récifs en plongée." },
    { title: "Étiquette", text: "Tenue modeste en ville. Saluez par 'salam' pour dire bonjour." },
  ],
  it: [
    { title: "Periodo migliore", text: "Ottobre – aprile. Primavera e autunno ideali per le immersioni." },
    { title: "Come arrivare", text: "Hurghada (HRG) 1h30 o Marsa Alam (RMF) 1h di auto." },
    { title: "Valuta", text: "Lira egiziana (EGP). USD/EUR ampiamente accettati." },
    { title: "Sicurezza", text: "Tranquilla, a misura di famiglia. Attenzione alla barriera in immersione." },
    { title: "Etichetta", text: "Vestirsi modestamente in città. Salutare con 'salam'." },
  ],
  ru: [
    { title: "Лучший сезон", text: "Октябрь – апрель. Весна и осень идеальны для дайвинга." },
    { title: "Как добраться", text: "Хургада (HRG) 1,5 ч или Марса-Алам (RMF) 1 ч езды." },
    { title: "Валюта", text: "Египетский фунт (EGP). USD/EUR принимают повсюду." },
    { title: "Безопасность", text: "Тихо и безопасно для семей. Берегите рифы при погружении." },
    { title: "Этикет", text: "Одевайтесь скромно в городе. 'Салам' — дружелюбное приветствие." },
  ],
  ar: [
    { title: "أفضل موسم", text: "أكتوبر – أبريل. الربيع والخريف مثاليان للغوص." },
    { title: "كيفية الوصول", text: "الغردقة (HRG) 1.5 ساعة أو مرسى علم (RMF) ساعة بالسيارة." },
    { title: "العملة", text: "الجنيه المصري (EGP). الدولار واليورو مقبولان على نطاق واسع." },
    { title: "الأمان", text: "هادئة ومناسبة للعائلات. احرص على حماية الشعاب أثناء الغوص." },
    { title: "آداب السلوك", text: "ارتدِ ملابس محتشمة في المدينة. ألقِ السلام بقول 'سلام'." },
  ],
  zh: [
    { title: "最佳季节", text: "10月至4月。春季和秋季最适合潜水。" },
    { title: "如何到达", text: "赫尔格达(HRG)1.5小时或马萨阿拉姆(RMF)1小时车程。" },
    { title: "货币", text: "埃及镑(EGP),美元/欧元广泛接受。" },
    { title: "安全", text: "宁静、适合家庭。潜水时请爱护珊瑚礁。" },
    { title: "礼仪", text: "在城里着装得体,用阿拉伯语'salam'友好问候。" },
  ],
  ja: [
    { title: "ベストシーズン", text: "10月~4月。春と秋はダイビングに最適。" },
    { title: "アクセス", text: "フルガダ(HRG)から1.5時間、またはマルサ・アラム(RMF)から1時間。" },
    { title: "通貨", text: "エジプトポンド(EGP)。USD/EURも広く使えます。" },
    { title: "安全", text: "静かで家族向け。ダイビング時はサンゴを守ろう。" },
    { title: "マナー", text: "街では控えめな服装で。'サラーム'と笑顔で挨拶を。" },
  ],
  da: [
    { title: "Bedste sæson", text: "Oktober – april. Forår og efterår er ideelle til dykning." },
    { title: "Sådan kommer du dertil", text: "Hurghada (HRG) 1,5 t eller Marsa Alam (RMF) 1 t kørsel." },
    { title: "Valuta", text: "Egyptisk pund (EGP). USD/EUR accepteres vidt omkring." },
    { title: "Sikkerhed", text: "Rolig, familievenlig. Vær revbevidst ved dyk." },
    { title: "Etikette", text: "Klæd dig beskedent i byen. Sig 'salam' som venlig hilsen." },
  ],
};

const PRACTICAL_LABEL = {
  en: "Practical", de: "Praktisch", fr: "Pratique", it: "Informazioni pratiche",
  ru: "Полезно", ar: "عملي", zh: "实用", ja: "実用情報", da: "Praktisk",
};

export default function TipsPage() {
  const { t, lang } = useI18n();
  const quick = QUICK_BY_LANG[lang] || QUICK_BY_LANG.en;
  const practical = PRACTICAL_LABEL[lang] || PRACTICAL_LABEL.en;

  return (
    <main className="min-h-screen" data-testid="tips-page">
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pt-28 pb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--brand-primary)] font-bold mb-4">{practical}</p>
        <h1 className="font-display text-stone-900 text-5xl sm:text-7xl tracking-tight">{t("section_tips")}</h1>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
        {quick.map((q, i) => {
          const Icon = ICONS[i];
          return (
            <div key={i} className="bg-white rounded-2xl border border-stone-200 p-6 hover:shadow-md transition" data-testid={`quick-tip-${i}`}>
              <Icon className="h-5 w-5 text-[var(--brand-primary)]" />
              <h3 className="font-display text-lg mt-3 text-stone-900">{q.title}</h3>
              <p className="text-sm text-stone-600 mt-2 leading-relaxed">{q.text}</p>
            </div>
          );
        })}
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <Prose section="tips" fallbackTitle={t("section_tips")} />
        </div>
        <div className="lg:col-span-4">
          <Prose section="culture" fallbackTitle={t("section_culture")} />
        </div>
      </section>
    </main>
  );
}
